import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"

// AI Content Assistant for the campaign Compose step. Sibling to
// contact-email-form; reads/writes the same Trix body + subject/preview fields
// by name. Calls POST /organizer/campaigns/generate_ai_content (synchronous,
// like the event AI summary endpoint) and applies the result.
//
// Actions:
//   generateCampaign  — build a full body from event + goal/tone/length + template
//   runAiAction       — rewrite the body via a menu action (data-ai-action)
//   customAction      — rewrite from a free-text instruction (the "Tell AI..." input)
//   suggestSubject / suggestPreview — fetch options and render a pickable list
export default class extends Controller {
  static targets = [
    "overlay",            // loading overlay over the editor
    "subjectSuggestions", // container for subject options (on Details)
    "previewSuggestions", // container for preview options (on Details)
    "metaSubject",        // read-only Subject shown in Compose
    "metaPreview",        // read-only Preview Text shown in Compose
    "generateBtn",        // Generate Campaign button
    "generateHint"        // helper line under it
  ]

  static values = {
    url: String,
    messages: Object // { failed }
  }

  get url() { return this.urlValue || "/organizer/campaigns/generate_ai_content" }

  connect() {
    this.mirrorMeta()
    this.gateGenerate()
    this._outside = (e) => this.closeOnOutsideClick(e)
    document.addEventListener("click", this._outside)
  }

  // Generate needs real inputs to produce good output, so it's disabled until
  // both an Event and a Campaign Type are selected.
  gateGenerate() {
    if (!this.hasGenerateBtnTarget) return
    const hasEvent = this.fieldValue("event_id").trim() !== ""
    const hasType = !!document.querySelector('input[name="campaign[category]"]:checked')
    const ready = hasEvent && hasType
    this.generateBtnTarget.disabled = !ready
    if (this.hasGenerateHintTarget) {
      this.generateHintTarget.textContent = ready
        ? this.generateHintTarget.dataset.readyText || this.generateHintTarget.textContent
        : (this.generateHintTarget.dataset.blockedText || this.generateHintTarget.textContent)
    }
  }

  disconnect() {
    if (this._outside) document.removeEventListener("click", this._outside)
  }

  // Mirror the Details Subject / Preview Text into the read-only header shown on
  // the Compose step (display only; edited on Details).
  mirrorMeta() {
    const fallback = "—"
    if (this.hasMetaSubjectTarget) {
      this.metaSubjectTarget.textContent = this.fieldValue("subject").trim() || fallback
    }
    if (this.hasMetaPreviewTarget) {
      this.metaPreviewTarget.textContent = this.fieldValue("preview_text").trim() || fallback
    }
  }

  // --- Foundation + field readers (query by name; shared with contact-email-form) ---
  field(name) { return document.querySelector(`[name="campaign[${name}]"]`) }
  fieldValue(name) { const el = this.field(name); return el ? el.value : "" }

  get bodyEditor() {
    const el = document.querySelector('trix-editor')
    return el && el.editor ? el : null
  }

  bodyHtml() {
    const el = this.bodyEditor
    return el ? el.innerHTML : (this.field("body")?.value || "")
  }

  foundation() {
    return {
      event_id: this.fieldValue("event_id"),
      tone: this.fieldValue("tone"),
      length: this.fieldValue("length"),
      // The selected Campaign Type (category) is the content angle; subject and
      // preview the user already wrote on Details steer the generated body too.
      category: document.querySelector('input[name="campaign[category]"]:checked')?.value || "",
      subject: this.fieldValue("subject"),
      preview_text: this.fieldValue("preview_text")
    }
  }

  // --- Generate a full campaign body ---------------------------------------
  async generateCampaign(event) {
    event?.preventDefault()
    await this.request({ mode: "generate", ...this.foundation() }, (data) => this.writeBody(data.content))
  }

  // --- Rewrite via menu action (Make shorter, more professional, ...) ------
  async runAiAction(event) {
    event?.preventDefault()
    const action = event.currentTarget.dataset.aiAction
    await this.request(
      { mode: "rewrite", action, body: this.bodyHtml(), ...this.foundation() },
      (data) => this.writeBody(data.content)
    )
  }

  // --- Custom free-text instruction ----------------------------------------
  async customAction(event) {
    event?.preventDefault()
    const input = event.currentTarget
    const instructions = input.value.trim()
    if (!instructions) return
    if (event.type === "keydown" && event.key !== "Enter") return
    await this.request(
      { mode: "rewrite", action: "custom", instructions, body: this.bodyHtml(), ...this.foundation() },
      (data) => { this.writeBody(data.content); input.value = "" }
    )
  }

  // --- Subject / Preview suggestions (Details step) ------------------------
  // The button toggles a popover anchored to the field. Open shows the prebuilt
  // starters immediately; the AI call then swaps in fresh, tailored options.
  async suggestSubject(event) {
    event?.preventDefault()
    if (this.togglePopover(this.subjectSuggestionsTarget)) return // just closed
    await this.request(
      { mode: "subject", body: this.bodyHtml(), ...this.foundation() },
      (data) => this.renderSuggestions(this.subjectSuggestionsTarget, data.suggestions, "subject"),
      event.currentTarget
    )
  }

  async suggestPreview(event) {
    event?.preventDefault()
    if (this.togglePopover(this.previewSuggestionsTarget)) return
    await this.request(
      { mode: "preview", subject: this.fieldValue("subject"), ...this.foundation() },
      (data) => this.renderSuggestions(this.previewSuggestionsTarget, data.suggestions, "preview_text"),
      event.currentTarget
    )
  }

  // Open the popover (showing whatever chips it already holds). Returns true if
  // the click closed an already-open popover (so the caller skips the AI fetch).
  togglePopover(container) {
    if (!container) return false
    if (!container.classList.contains("d-none")) { container.classList.add("d-none"); return true }
    // Close any other open suggestion popover first.
    this.element.querySelectorAll(".ai-suggestions-popover").forEach((p) => p.classList.add("d-none"))
    container.classList.remove("d-none")
    return false
  }

  // --- Shared request + loading/error UX -----------------------------------
  async request(body, onSuccess, trigger = null) {
    this.showLoading(trigger)
    try {
      const response = await post(this.url, { body, responseKind: "json" })
      if (response.ok) {
        onSuccess(await response.json)
      } else {
        this.flashError()
      }
    } catch (e) {
      this.flashError()
    } finally {
      this.hideLoading(trigger)
    }
  }

  writeBody(html) {
    if (!html) return
    const el = this.bodyEditor
    if (el && el.editor) {
      el.editor.loadHTML(html)
    } else if (this.field("body")) {
      this.field("body").value = html
    }
  }

  // Fill the field from a clicked suggestion chip (prebuilt or AI-generated),
  // then close the popover.
  applySuggestion(event) {
    const btn = event.currentTarget
    const fieldName = btn.dataset.field
    const f = this.field(fieldName)
    if (f) { f.value = btn.textContent.trim(); f.dispatchEvent(new Event("input", { bubbles: true })) }
    btn.closest(".ai-suggestions-popover")?.classList.add("d-none")
  }

  // Close suggestion popovers when clicking outside them / their trigger.
  closeOnOutsideClick(event) {
    if (event.target.closest(".ai-suggestions-popover") || event.target.closest(".ai-suggest-btn")) return
    this.element.querySelectorAll(".ai-suggestions-popover").forEach((p) => p.classList.add("d-none"))
  }

  // Replace a container's chips with fresh AI options (same chip markup +
  // applySuggestion action as the prebuilt ones, so behavior is identical).
  renderSuggestions(container, suggestions, fieldName) {
    if (!container) return
    container.innerHTML = ""
    if (!suggestions || !suggestions.length) { container.classList.add("d-none"); return }
    container.classList.remove("d-none")
    suggestions.forEach((text) => {
      const btn = document.createElement("button")
      btn.type = "button"
      btn.className = "ai-suggestion-chip"
      btn.dataset.field = fieldName
      btn.dataset.action = "click->campaign-ai#applySuggestion"
      btn.textContent = text
      container.appendChild(btn)
    })
  }

  showLoading(trigger) {
    if (this.hasOverlayTarget) this.overlayTarget.classList.remove("d-none")
    if (trigger) { trigger.disabled = true; trigger.classList.add("is-busy") }
  }

  hideLoading(trigger) {
    if (this.hasOverlayTarget) this.overlayTarget.classList.add("d-none")
    if (trigger) { trigger.disabled = false; trigger.classList.remove("is-busy") }
  }

  flashError() {
    if (typeof window.displayFlashMessage === "function") {
      window.displayFlashMessage("error", this.messagesValue?.failed || "AI request failed. Please try again.")
    }
  }
}
